import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Alert, CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@mui/material'
import { Close as CloseIcon, OpenInFull, Output, ExitToApp } from '@mui/icons-material'
import moment from 'moment'

import { Expansible } from 'components/Expansible'
import { Modal } from 'components'
import { get } from 'services/api'
import { formatDuration } from 'utils/functions'
import styles from './styles'

const formatDate = (value) => (
  value && moment(value).isValid() ? moment(value).format('DD/MM/YYYY HH:mm:ss') : 'N/A'
)

const formatJson = (value) => JSON.stringify(value ?? {}, null, 2)

const UserActivityLogDetails = ({ uuid, open, onClose }) => {
  const classes = styles()
  const token = useSelector((state) => state.auth.access_token)
  const [activity, setActivity] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [expandedJson, setExpandedJson] = useState(null)
  
  useEffect(() => {
    if (!open || !uuid) return undefined

    let isActive = true
    const loadActivity = async () => {
      setLoading(true)
      setError(false)
      setActivity(null)
      const response = await get({
        url: `/user-activity/${uuid}`,
        token,
      })
      if (isActive) {
        setActivity(response)
        setError(!response)
        setLoading(false)
      }
    }

    loadActivity()
    return () => {
      isActive = false
    }
  }, [open, token, uuid])

  const fields = activity ? [
    ['Identificador', activity.uuid],
    ['Data e hora', formatDate(activity.timestamp)],
    ['Ação', activity.action_description || activity.action],
    ['Método HTTP', activity.http_method],
    ['Origem da requisição', activity.requested_from],
    ['Endereço IP', activity.ip_address],
    ['Resultado HTTP', activity.http_status_result],
    ['Resultado da atividade', activity.was_successful === true ? 'Sucesso' : activity.was_successful === false ? 'Falha' : null],
    ['Processamento', activity.is_completed === true ? 'Concluído' : activity.is_completed === false ? 'Em andamento' : null],
    ['Duração', formatDuration(activity.request_duration_ms) || 'N/A' ],
    ['Usuário', activity.user?.name],
    ['E-mail', activity.user?.email],
    ['Perfil', activity.user?.user_role],
    ['ID do usuário', activity.user?.uuid],
    ['Último acesso', formatDate(activity.user?.last_login_at)],
    ['Último logout solicitado', formatDate(activity.user?.last_requested_logout_at)],
    ['ID da sessão', activity.user_session_id],
  ] : []

  const renderJsonBlock = (title, value) => {
    const json = formatJson(value)
    return (
      <div className={classes.jsonContainer}>
        <IconButton
          className={classes.expandButton}
          aria-label={`Ampliar ${title}`}
          title={`Ampliar ${title}`}
          onClick={() => setExpandedJson(json)}
        >
          <OpenInFull />
        </IconButton>
        <pre className={classes.json}>{json}</pre>
      </div>
    )
  }

  return (
    <Modal title="Detalhes da atividade" open={open} onClose={onClose}>
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
          <Typography>Carregando detalhes da atividade...</Typography>
        </div>
      ) : error ? (
        <Alert severity="error">Não foi possível carregar os detalhes desta atividade.</Alert>
      ) : activity ? (
        <div className={classes.root}>
          <section aria-label="Informações básicas">
            <dl className={classes.fields}>
              {fields.map(([label, value]) => (
                <div className={classes.field} key={label}>
                  <dt>{label}</dt>
                  <dd>{value === null || value === undefined || value === '' ? 'N/A' : value}</dd>
                </div>
              ))}
            </dl>
          </section>
          <br />
          <Expansible title="Dados de Input (Request)" titleIcon={<ExitToApp fontSize='small' style={{ marginRight: 5 }} />} open={true}>
            {renderJsonBlock('Request', activity.input)}
          </Expansible>
          <Expansible title="Dados de Output (Response)" titleIcon={<Output fontSize='small' style={{ transform: 'scaleX(-1)', marginRight: 5 }} />}>
            {renderJsonBlock('Response', activity.output)}
          </Expansible>
        </div>
      ) : null}
      <Dialog
        open={expandedJson !== null}
        onClose={() => setExpandedJson(null)}
        fullScreen
      >
        <DialogContent className={classes.expandedDialogContent}>
          <IconButton
            className={classes.closeExpandedButton}
            aria-label="Fechar visualização ampliada"
            title="Fechar"
            onClick={() => setExpandedJson(null)}
          >
            <CloseIcon />
          </IconButton>
          <pre className={classes.expandedJson}>{expandedJson}</pre>
        </DialogContent>
      </Dialog>
    </Modal>
  )
}

export default UserActivityLogDetails
